import { deleteDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from './firebase';
import { ProcessVideoRequest, ProcessVideoResponse } from '../types';

/**
 * Step 1: Create job and get signed upload URL
 * This calls the processVideo Cloud Function which creates a Firestore job
 * and returns a signed URL for uploading the video directly to GCS
 */
export async function createVideoJob(request: ProcessVideoRequest): Promise<ProcessVideoResponse> {
  const processVideo = httpsCallable<ProcessVideoRequest, ProcessVideoResponse>(
    functions,
    'processVideo'
  );

  const result = await processVideo(request);
  return result.data;
}

/**
 * Step 2: Upload video file to GCS using signed URL
 * This uploads directly to Google Cloud Storage without going through Firebase Storage
 * The upload triggers the onVideoUploaded Cloud Function which starts processing
 */
export async function uploadVideoToGCS(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const progress = (e.loaded / e.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
      }
    });

    xhr.addEventListener('error', (e) => {
      reject(new Error(`Upload failed: ${e.toString()}`));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

/**
 * Cancel an upload in progress
 */
export function createCancellableUpload(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void
) {
  const xhr = new XMLHttpRequest();
  let cancelled = false;

  const promise = new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress && !cancelled) {
        const progress = (e.loaded / e.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (cancelled) {
        reject(new Error('Upload cancelled'));
      } else if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });

  return {
    promise,
    cancel: () => {
      cancelled = true;
      xhr.abort();
    },
  };
}

/**
 * Delete a job document from Firestore.
 * Firestore security rules allow the authenticated owner to delete their own job.
 */
export async function deleteVideoJob(jobId: string): Promise<void> {
  await deleteDoc(doc(db, 'jobs', jobId));
}
